// RevenueCat Web Billing wrapper.
//
// THE ONE RULE IN THIS FILE: every touch of the SDK goes through
// `await import('@revenuecat/purchases-js')` INSIDE a function body. There is
// no top-level runtime import, so:
//   - a user who never opens the paywall still never downloads the
//     SDK.
// The only top-level import is `import type`, which verbatimModuleSyntax
// erases outright.
//
// Configuration is env-driven and OFF by default: without VITE_RC_PUBLIC_KEY
// `isRevenueCatConfigured()` is false and the paywall stays on its static
// "Available soon" plans. Switching purchasing on is an env-only change.
//
// Ordering contract: `configureRc(userId)` MUST resolve before
// getProOffering / purchasePro / getSubscriptionSummary. Those three call
// `Purchases.getSharedInstance()`, which throws UninitializedPurchasesError
// when the SDK has never been configured.

import type { Package } from '@revenuecat/purchases-js'
import { androidAppBillingCapable, isAndroidApp } from './androidApp'
import { readDeviceKey } from './deviceKey'

export type { Package as RcPackage } from '@revenuecat/purchases-js'

// The app_user_id currently bound to the SDK. The SDK is configure-once per
// page load, so this cache is what distinguishes "first configure" from
// "the signed-in user changed" (see configureRc).
let configuredAppUserId: string | null = null

// Serializes overlapping configureRc calls. React StrictMode runs effects
// twice in dev, so two calls can race the dynamic import and both decide the
// SDK is unconfigured; without this they would call configure() twice.
let inFlight: Promise<void> | null = null

export const RC_NO_USER_ERROR =
  'configureRc requires a signed-in user id (the RevenueCat app_user_id is the Supabase user id)'
export const RC_UNCONFIGURED_ERROR =
  'RevenueCat is not configured (VITE_RC_PUBLIC_KEY is unset)'
export const RC_ANDROID_APP_ERROR =
  'Purchases are not offered inside the Android app'

/**
 * May this client start a NEW WEB purchase at all?
 *
 * False inside the Android app (a Trusted Web Activity): Play requires Play
 * billing for digital goods sold in the app, so the web checkout never runs
 * there. A billing-capable app build sells through the native Play paywall
 * instead (purchaseSurface below, which is what every upgrade entry point
 * checks). getProOffering / purchasePro refuse on this check on their own,
 * so an entry point that gets its own check wrong still cannot run the web
 * checkout inside the app.
 *
 * Deliberately NOT folded into isRevenueCatConfigured: a subscriber inside
 * the wrapper still needs configureRc + getSubscriptionSummary for the
 * manage/cancel link on the account page.
 */
export function webPurchaseAllowed(): boolean {
  return !isAndroidApp()
}

/**
 * Can this document sell Pro through Google Play (the native paywall)?
 *
 * All four must hold: we are inside the Android app; the app build is
 * billing-capable (`billing=1`, honored only in the app, see
 * androidAppBillingCapable); the head bootstrap did not turn billing off for
 * this document; and a well-formed device key is stored (a link token is
 * bound to its hash, so without one nothing could ever resolve).
 */
export function androidBillingAvailable(): boolean {
  if (!isAndroidApp()) return false
  if (!androidAppBillingCapable()) return false
  return readDeviceKey() !== null
}

export type PurchaseSurface = 'web' | 'play' | 'none'

/**
 * WHERE this client sells Pro, which is what every upgrade entry point
 * checks:
 *   - 'web'  in a browser: RevenueCat Web Billing, exactly as before.
 *   - 'play' inside a billing-capable Android app build: the sheet shows
 *     AndroidPlayPurchasePanel, which hands off to the native Play paywall.
 *   - 'none' inside an older Android app build (or one without a usable
 *     device key): no upgrade entry points at all, exactly as before.
 * The web checkout stays refused inside the app whatever this says
 * (getProOffering / purchasePro check webPurchaseAllowed on their own).
 */
export function purchaseSurface(): PurchaseSurface {
  // eslint-disable-next-line i18next/no-literal-string -- purchase surface code
  if (!isAndroidApp()) return 'web'
  // eslint-disable-next-line i18next/no-literal-string -- purchase surface code
  return androidBillingAvailable() ? 'play' : 'none'
}

/**
 * Whether a paywall sheet opened right now can actually sell: Play inside a
 * billing-capable app, or the web checkout when RevenueCat is configured.
 * The old `isRevenueCatConfigured() && webPurchaseAllowed()` slot predicate,
 * extended by the Play surface.
 */
export function paywallCanSell(): boolean {
  const surface = purchaseSurface()
  // eslint-disable-next-line i18next/no-literal-string -- purchase surface code
  if (surface === 'play') return true
  // eslint-disable-next-line i18next/no-literal-string -- purchase surface code
  return surface === 'web' && isRevenueCatConfigured()
}

/**
 * Whether web purchasing is switched on for this build.
 *
 * Read at CALL time, not module load, so tests can stub the env and so the
 * value cannot be captured into a stale module constant.
 */
export function isRevenueCatConfigured(): boolean {
  return !!readApiKey()
}

function readApiKey(): string | undefined {
  const key = import.meta.env.VITE_RC_PUBLIC_KEY as string | undefined
  return key ? key : undefined
}

/**
 * Binds the SDK to a signed-in user.
 *
 * Never called without a user id: the RevenueCat app_user_id MUST equal the
 * Supabase user id, and configuring against an anonymous id would create a
 * RevenueCat customer that the server's entitlement webhook can never match to
 * an account. Callers gate on a resolved session; an empty id is a bug and
 * throws rather than silently configuring something wrong.
 *
 * Semantics:
 *   - no cached id  -> Purchases.configure({ apiKey, appUserId })
 *   - same id       -> no-op
 *   - different id  -> Purchases.getSharedInstance().changeUser(userId)
 *     (user A signed out and user B signed in without a page reload)
 */
export async function configureRc(userId: string): Promise<void> {
  if (!userId) throw new Error(RC_NO_USER_ERROR)
  const apiKey = readApiKey()
  if (!apiKey) throw new Error(RC_UNCONFIGURED_ERROR)

  // Chain onto any in-flight call. A previous failure must not poison the
  // chain, hence the swallowing catch: this call reports its own outcome.
  const run = (inFlight ?? Promise.resolve())
    .catch(() => {})
    .then(() => applyUser(apiKey, userId))
  inFlight = run
  try {
    await run
  } finally {
    if (inFlight === run) inFlight = null
  }
}

async function applyUser(apiKey: string, userId: string): Promise<void> {
  if (configuredAppUserId === userId) return
  const { Purchases } = await import('@revenuecat/purchases-js')
  if (configuredAppUserId === null) {
    Purchases.configure({ apiKey, appUserId: userId })
  } else {
    await Purchases.getSharedInstance().changeUser(userId)
  }
  configuredAppUserId = userId
}

/**
 * TEST ONLY. Forgets which user the SDK is bound to, which is what a page
 * reload does. Tests need it because the cache is module state that outlives
 * an individual test, and because the SDK mock has to model the real
 * "getSharedInstance throws before configure" contract in lockstep with it.
 */
export function __resetRcCacheForTests(): void {
  configuredAppUserId = null
  inFlight = null
}

export interface ProOffering {
  monthly: Package | null
  annual: Package | null
}

/**
 * The two plans of the CURRENT offering, mapped by package type.
 *
 * Missing plans come back as null rather than throwing: an offering with only
 * one configured package is a valid dashboard state and the paywall renders
 * whatever it gets. No current offering at all yields two nulls.
 */
export async function getProOffering(): Promise<ProOffering> {
  if (!webPurchaseAllowed()) throw new Error(RC_ANDROID_APP_ERROR)
  const { Purchases, PackageType } = await import('@revenuecat/purchases-js')
  const offerings = await Purchases.getSharedInstance().getOfferings()
  const current = offerings?.current
  if (!current) return { monthly: null, annual: null }
  return {
    // eslint-disable-next-line i18next/no-literal-string -- package id or result code
    monthly: pickPackage(current.availablePackages, current.monthly, PackageType.Monthly, 'monthly'),
    // eslint-disable-next-line i18next/no-literal-string -- package id or result code
    annual: pickPackage(current.availablePackages, current.annual, PackageType.Annual, 'annual'),
  }
}

// The Offering convenience accessor is the source of truth; the scans are
// belt-and-braces for a dashboard that defined the plan as a custom package
// (packageType Custom) under a recognizable identifier.
function pickPackage(
  available: Package[] | undefined,
  direct: Package | null | undefined,
  type: Package['packageType'],
  identifierHint: string,
): Package | null {
  if (direct) return direct
  const list = available ?? []
  const byType = list.find((pkg) => pkg.packageType === type)
  if (byType) return byType
  const hint = identifierHint.toLowerCase()
  return list.find((pkg) => pkg.identifier?.toLowerCase().includes(hint)) ?? null
}

export type PurchaseOutcome = 'purchased' | 'cancelled'

/**
 * Runs the RevenueCat checkout for a package.
 *
 * Returns 'cancelled' when the user closed the checkout, which is a normal
 * outcome and not an error the UI should shout about. Everything else
 * rethrows: a real failure must not be swallowed into a silent no-op, and a
 * completed charge must never be reported as a failure.
 */
export async function purchasePro(pkg: Package): Promise<PurchaseOutcome> {
  // Fail closed, before the SDK is even loaded: see webPurchaseAllowed.
  if (!webPurchaseAllowed()) throw new Error(RC_ANDROID_APP_ERROR)
  const { Purchases, ErrorCode, PurchasesError } = await import('@revenuecat/purchases-js')
  try {
    await Purchases.getSharedInstance().purchase({ rcPackage: pkg })
    // eslint-disable-next-line i18next/no-literal-string -- package id or result code
    return 'purchased'
  } catch (err) {
    // The SDK rejects with a PurchasesError carrying
    // ErrorCode.UserCancelledError when the customer closes the checkout.
    // eslint-disable-next-line i18next/no-literal-string -- package id or result code
    if (isUserCancelled(err, ErrorCode?.UserCancelledError, PurchasesError)) return 'cancelled'
    throw err
  }
}

// Two guards, because this is the money path: swallowing a real failure as a
// "cancellation" hides a broken checkout, so an unrelated error object that
// merely happens to carry errorCode 1 must NOT qualify. Both the code and the
// class have to match (the class comes from the same dynamic import, so there
// is exactly one copy of it).
function isUserCancelled(err: unknown, cancelCode: unknown, ErrorClass: unknown): boolean {
  if (cancelCode === undefined || cancelCode === null) return false
  if (typeof err !== 'object' || err === null) return false
  if ((err as { errorCode?: unknown }).errorCode !== cancelCode) return false
  if (typeof ErrorClass === 'function') return err instanceof ErrorClass
  return false
}

/**
 * The entitlement identifier configured in the RevenueCat dashboard. It is an
 * OPERATOR-CREATED value (see the flip checklist in
 * docs/superpowers/plans/2026-07-28-pro-web-client.md), so until that step is
 * done RevenueCat reports no such entitlement and `willRenew` below is null.
 */
export const PRO_ENTITLEMENT_ID = 'pro'

export interface SubscriptionSummary {
  /**
   * The customer's subscription management URL, or null when there is none.
   *
   * Present for a web-billed subscription (and for a store-billed one
   * RevenueCat points at the relevant store).
   */
  managementUrl: string | null
  /**
   * Whether the subscription is set to renew at the end of the current period.
   *
   * NULL means unknown, not "no": RevenueCat has no `pro` entitlement for this
   * customer, which is also the state before the entitlement is configured at
   * all. Callers must not render a null as "will not renew" - saying access is
   * ending to somebody who is about to be charged is the worse error of the
   * two.
   */
  willRenew: boolean | null
  /**
   * Whether the customer is inside a FREE TRIAL. Null when unknown, same rule
   * as willRenew.
   *
   * This changes what the expiry date MEANS. RevenueCat's expirationDate is
   * the trial's end during a trial, so "renews on" would describe a first
   * charge as a renewal - which is the exact thing the Terms spell out
   * separately ("Free trials convert to a paid subscription unless cancelled
   * before the trial ends").
   */
  isTrial: boolean | null
}

/**
 * One customer-info round trip answering both questions the account page and
 * the paywall ask. Kept as a single call deliberately: two exported readers
 * would mean two network reads per render and two chances to disagree.
 */
export async function getSubscriptionSummary(): Promise<SubscriptionSummary> {
  const { Purchases } = await import('@revenuecat/purchases-js')
  const info = await Purchases.getSharedInstance().getCustomerInfo()
  const pro = info?.entitlements?.active?.[PRO_ENTITLEMENT_ID]
  return {
    managementUrl: info?.managementURL ?? null,
    willRenew: pro ? pro.willRenew : null,
    isTrial: pro ? pro.periodType === 'trial' : null,
  }
}
